// UPLOAD-ORIGINAL (founder 2026-10-30): on a doc made from an uploaded file, the "Original" bar
// above its body shows the file — name, type, size, pages, when it was uploaded, its text status —
// with visible icons: Preview (a PDF or text file opens inline; a Word file downloads), Download,
// Download .md, and for the doc's managers (`can_manage`) Replace file (the old one kept as an
// earlier version; the text read again in the background, a new version of the doc), Re-extract,
// Remove original (the text stays) and Make plain doc — both asking first, both for good. Earlier
// versions list in their own modal with a download each. Retry stays for editors when a read
// failed ("Couldn't read the text: retry").
import { PAGE_UPLOAD_LIMITS } from "@companyos/schema/contracts";
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoTip,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DownloadIcon,
  EyeIcon,
  FileDownIcon,
  FileTextIcon,
  HistoryIcon,
  RefreshCcwIcon,
  RotateCwIcon,
  Trash2Icon,
  UnlinkIcon,
  UploadIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { RowAction } from "../settings/row-actions";
import { problem, processingLabel, size, UPLOAD_STATUS, uploadKind } from "./doc-upload";
import type { PageDetail } from "./page-dialogs";
import { invalidateWork, workKeys } from "./work-queries";

type Upload = NonNullable<PageDetail["upload"]>;

async function fetchFile(
  api: Api,
  co: string,
  id: string,
  as: "original" | "markdown",
  revision?: number,
): Promise<Blob> {
  return (await api.client.pages.uploadContent({
    params: { co, id },
    query: { as, download: "true", ...(revision ? { revision } : {}) },
  })) as Blob;
}

async function save(
  api: Api,
  co: string,
  id: string,
  as: "original" | "markdown",
  name: string,
  revision?: number,
) {
  const url = URL.createObjectURL(await fetchFile(api, co, id, as, revision));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** What Preview can show inline (a PDF, or text); anything else downloads. */
const previewable = (mime: string) => mime === "application/pdf" || mime.startsWith("text/");

/** The original shown in a modal: a PDF in a frame, a text file as text. */
function PreviewDialog({
  api,
  company,
  page,
  upload,
  open,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  page: PageDetail;
  upload: Upload;
  open: boolean;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let gone = false;
    let made: string | null = null;
    setError(null);
    fetchFile(api, company.id, page.id, "original")
      .then(async (blob) => {
        if (gone) return;
        if (upload.mime === "application/pdf") {
          made = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
          setUrl(made);
        } else setText(await blob.text());
      })
      .catch((e) => !gone && setError(problem(e, "The file could not be opened.")));
    return () => {
      gone = true;
      if (made) URL.revokeObjectURL(made);
      setUrl(null);
      setText(null);
    };
  }, [open, api, company.id, page.id, upload.mime]);
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="flex h-[85dvh] flex-col sm:max-w-4xl" data-slot="original-preview">
        <DialogHeader>
          <DialogTitle>{upload.file_name}</DialogTitle>
          <DialogDescription>
            {uploadKind(upload.mime)} · {size(upload.bytes)}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p className="m-0 text-sm text-danger-text">{error}</p>
        ) : url ? (
          <iframe
            title={`Preview of ${upload.file_name}`}
            src={url}
            className="min-h-0 w-full flex-1 rounded-md border border-border"
          />
        ) : text !== null ? (
          <pre className="m-0 min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-sunken p-3 text-sm">
            {text}
          </pre>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">Opening…</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The earlier originals Replace file kept, each with a download. */
function VersionsDialog({
  api,
  company,
  page,
  upload,
  open,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  page: PageDetail;
  upload: Upload;
  open: boolean;
  onClose: () => void;
}) {
  const older = [...(upload.previous_versions ?? [])].reverse();
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-lg" data-slot="original-versions">
        <DialogHeader>
          <DialogTitle>Earlier files</DialogTitle>
          <DialogDescription>Replaced originals of this doc, newest first.</DialogDescription>
        </DialogHeader>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {older.map((v) => (
            <li
              key={v.revision}
              className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm"
            >
              <span className="w-7 shrink-0 text-xs text-muted-foreground tabular-nums">
                v{v.revision}
              </span>
              <span className="min-w-0 flex-1 truncate">{v.file_name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {size(v.bytes)} · {day(v.uploaded_at)}
              </span>
              <RowAction
                icon={DownloadIcon}
                label={`Download ${v.file_name} (v${v.revision})`}
                tip="Download"
                onClick={() =>
                  void save(api, company.id, page.id, "original", v.file_name, v.revision)
                }
              />
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

/** The bar above an uploaded page's body: the original file, its status and its actions. */
export function UploadSourceBar({
  api,
  company,
  page,
}: {
  api: Api;
  company: CompanyAccess;
  page: PageDetail;
}) {
  const queryClient = useQueryClient();
  const co = company.id;
  const picker = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState(false);
  const [versions, setVersions] = useState(false);
  const settle = async (next: PageDetail, title: string) => {
    queryClient.setQueryData(workKeys.page(co, page.id), next);
    await invalidateWork(queryClient, co);
    toast.add({ title, type: "success" });
  };
  const failed = (fallback: string) => (e: unknown) =>
    toast.add({ title: problem(e, fallback) ?? fallback, type: "error" });
  const retry = useMutation({
    mutationFn: () =>
      api.client.pages.uploadRetry({
        params: { co, id: page.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    // UPLOAD-PRIVATE-FIRST: the read runs in the background; the doc fills in place.
    onSuccess: (next) => settle(next, "Reading the text again…"),
    onError: failed("Retry failed"),
  });
  const reextract = useMutation({
    mutationFn: () =>
      api.client.pages.uploadReextract({
        params: { co, id: page.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (next) => settle(next, "Reading the text again: it becomes a new version"),
    onError: failed("Re-extract failed"),
  });
  const replace = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file, file.name);
      return api.client.pages.uploadReplace({
        params: { co, id: page.id },
        idempotencyKey: newIdempotencyKey(),
        body: form as never,
      });
    },
    onSuccess: (next) => settle(next, `${next.upload?.file_name ?? "File"} replaces the original`),
    onError: failed("The file could not be replaced"),
  });
  const remove = useMutation({
    mutationFn: () => api.client.pages.uploadRemoveOriginal({ params: { co, id: page.id } }),
    onSuccess: (next) => settle(next, "Original removed; the text stays"),
    onError: failed("The original could not be removed"),
  });
  const detach = useMutation({
    mutationFn: () => api.client.pages.uploadDetach({ params: { co, id: page.id } }),
    onSuccess: (next) => settle(next, "Now a plain doc"),
    onError: failed("The doc could not be made plain"),
  });
  const u = page.upload;
  if (!u) return null;
  const busy =
    retry.isPending ||
    reextract.isPending ||
    replace.isPending ||
    remove.isPending ||
    detach.isPending;
  const removed = Boolean(u.original_removed_at);
  const reading = u.status === "processing" || retry.isPending || reextract.isPending;
  const status = UPLOAD_STATUS[reading ? "processing" : u.status];
  const md = `${u.file_name.replace(/\.[A-Za-z0-9]{1,10}$/, "") || "document"}.md`;
  const uploaded = u.original_uploaded_at ?? u.created_at;
  const older = u.previous_versions?.length ?? 0;
  const manage = page.can_manage;
  return (
    <section
      aria-label="Original file"
      data-slot="upload-source"
      className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border bg-sunken px-3 py-1.5 text-sm"
    >
      <FileTextIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
      <span className="min-w-0 truncate font-medium" data-slot="original-name">
        {u.file_name}
      </span>
      <Badge variant="outline">{uploadKind(u.mime)}</Badge>
      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
        {size(u.bytes)}
        {u.source_pages ? ` · ${u.source_pages} ${u.source_pages === 1 ? "page" : "pages"}` : ""}
        {` · uploaded ${day(uploaded)}`}
        {u.revision > 1 ? ` · v${u.revision}` : ""}
      </span>
      {removed ? (
        <Badge variant="neutral" data-slot="original-removed">
          Original removed
        </Badge>
      ) : u.status === "failed" && page.can_edit && !reading ? (
        <button
          type="button"
          data-slot="upload-retry-chip"
          onClick={() => retry.mutate()}
          title={u.error ?? undefined}
          className="rounded-full border border-danger-text/30 bg-danger-soft px-2 py-0.5 text-xs font-medium text-danger-text hover:bg-danger-soft/70"
        >
          Processing failed — Retry
        </button>
      ) : (
        <Badge variant={status.variant} data-slot="upload-status">
          {reading
            ? processingLabel(u.status === "processing" ? u.progress : undefined)
            : status.label}
        </Badge>
      )}
      {u.status === "failed" && u.error && !reading ? (
        <span className="min-w-0 truncate text-xs text-danger-text" data-slot="upload-error">
          {u.error}
        </span>
      ) : null}
      {u.truncated ? <Badge variant="neutral">Shortened</Badge> : null}
      <InfoTip label="About the original file">
        {removed
          ? "The original file was removed; the doc's text stays. Replace file uploads a new one."
          : u.status === "done"
            ? `This doc is the text of ${u.file_name}, as Markdown. Agents read it with read_doc.${u.truncated ? " The doc holds the first part; the .md download has all of it." : ""}`
            : u.status === "processing"
              ? "The file is kept; its text is being read in the background and appears here when done."
              : u.status === "no_text"
                ? "No text layer: likely a scan. There is no OCR yet, so there is nothing to read. The original is kept."
                : `The text could not be read${u.error ? `: ${u.error}` : ""}. Retry reads the original again.`}
      </InfoTip>
      <span className="ml-auto flex items-center gap-0.5" data-slot="original-actions">
        {removed ? null : (
          <RowAction
            icon={EyeIcon}
            label={previewable(u.mime) ? "Preview original" : "Open original (download)"}
            tip={previewable(u.mime) ? "Preview" : "Open (downloads)"}
            onClick={() =>
              previewable(u.mime)
                ? setPreview(true)
                : void save(api, co, page.id, "original", u.file_name)
            }
          />
        )}
        {removed ? null : (
          <RowAction
            icon={DownloadIcon}
            label="Download original"
            onClick={() => void save(api, co, page.id, "original", u.file_name)}
          />
        )}
        {u.status === "done" ? (
          <RowAction
            icon={FileDownIcon}
            label="Download .md"
            onClick={() => void save(api, co, page.id, "markdown", md)}
          />
        ) : null}
        {older ? (
          <RowAction
            icon={HistoryIcon}
            label={`Earlier files (${older})`}
            onClick={() => setVersions(true)}
          />
        ) : null}
        {u.status !== "done" && !manage && page.can_edit && !removed ? (
          <RowAction
            icon={RotateCwIcon}
            label="Retry"
            disabled={busy}
            onClick={() => retry.mutate()}
          />
        ) : null}
        {manage ? (
          <>
            <input
              ref={picker}
              type="file"
              className="sr-only"
              aria-label="New original file"
              tabIndex={-1}
              accept={PAGE_UPLOAD_LIMITS.accept}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) replace.mutate(f);
              }}
            />
            <RowAction
              icon={UploadIcon}
              label="Replace file"
              tip="Replace file: the old one is kept as an earlier file"
              disabled={busy}
              onClick={() => picker.current?.click()}
            />
            {removed ? null : (
              <RowAction
                icon={RefreshCcwIcon}
                label="Re-extract text"
                tip="Read the text again (a new version of the doc)"
                disabled={busy || reading}
                onClick={() => reextract.mutate()}
              />
            )}
            {removed ? null : (
              <ConfirmRowAction
                icon={Trash2Icon}
                label="Remove original"
                title={`Remove ${u.file_name}?`}
                description="The original file and its earlier files are deleted for good; this can't be undone. The doc's text stays."
                confirmLabel="Remove original"
                disabled={busy}
                onConfirm={() => remove.mutate()}
              />
            )}
            <ConfirmRowAction
              icon={UnlinkIcon}
              label="Make plain doc"
              tip="Make a plain doc (detach the file)"
              title="Make this a plain doc?"
              description="The original file, its earlier files and the .md are deleted for good; this can't be undone. The doc's text stays as it is."
              confirmLabel="Make plain doc"
              disabled={busy}
              onConfirm={() => detach.mutate()}
            />
          </>
        ) : null}
      </span>
      <PreviewDialog
        api={api}
        company={company}
        page={page}
        upload={u}
        open={preview}
        onClose={() => setPreview(false)}
      />
      {older ? (
        <VersionsDialog
          api={api}
          company={company}
          page={page}
          upload={u}
          open={versions}
          onClose={() => setVersions(false)}
        />
      ) : null}
    </section>
  );
}
